import React, { useCallback, useEffect, useState } from "react";
import { X, PaperPlaneRight, Microphone } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import VoiceBubble from "./VoiceBubble";
import VoiceRecorderSheet from "./VoiceRecorderSheet";

export default function CommentsDrawer({ post, onClose, onAdded }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [pickVoice, setPickVoice] = useState(false);

  const load = useCallback(async () => {
    if (!post) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/posts/${post.id}/comments`);
      setItems(data);
    } catch (err) {
      console.error("comments load failed", err);
    } finally { setLoading(false); }
  }, [post]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/comments`, { text });
      setItems((prev) => [data, ...prev]);
      setText("");
      onAdded?.();
    } finally { setSending(false); }
  };

  const sendVoice = async (clip) => {
    setPickVoice(false);
    try {
      const { data } = await api.post(`/posts/${post.id}/comments`, { text: "", voice_path: clip.path, voice_duration: clip.duration, voice_title: clip.title });
      setItems((prev) => [data, ...prev]);
      onAdded?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not post voice comment"); }
  };

  if (!post) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-end" data-testid="comments-drawer">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-h-[85vh] crystal-glass rounded-t-3xl border-t border-x border-white/10 animate-fade-up flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <h3 className="text-cream text-xl font-black tracking-tight">Comments</h3>
            <p className="text-cream/50 text-xs tracking-[0.2em] uppercase">{post.comments_count || items.length} replies</p>
          </div>
          <button onClick={onClose} data-testid="comments-close" className="p-2 rounded-full hover:bg-white/5">
            <X size={22} className="text-cream" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading && <p className="text-cream/50 text-sm">Loading…</p>}
          {!loading && items.length === 0 && (
            <p className="text-cream/40 text-sm">Be the first to comment.</p>
          )}
          {items.map((c) => (
            <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
              <div className="w-9 h-9 rounded-full overflow-hidden bg-gradient-to-br from-papaya to-sea flex items-center justify-center text-ink font-black flex-shrink-0">
                {c.author?.avatar_path ? (
                  <img src={fileUrl(c.author.avatar_path)} alt="" className="w-full h-full object-cover" />
                ) : (c.author?.display_name || "?")[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-cream text-sm font-bold">@{c.author?.username || "user"}</p>
                {c.voice_path ? (
                  <div className="mt-1"><VoiceBubble url={c.voice_url || `/api/files/${c.voice_path}`} duration={c.voice_duration} title={c.voice_title} testid={`comment-voice-${c.id}`} /></div>
                ) : (
                  <p className="text-cream/80 text-sm leading-relaxed">{c.text}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {user && (
          <form onSubmit={submit} className="p-4 pr-24 sm:pr-4 pb-24 sm:pb-4 border-t border-white/10 flex gap-2">
            <button type="button" onClick={() => setPickVoice(true)} data-testid="comment-voice-btn" title="Voice comment" className="w-12 h-12 rounded-full bg-white/5 border border-white/10 text-papaya flex items-center justify-center flex-shrink-0">
              <Microphone size={20} weight="fill" />
            </button>
            <input
              data-testid="comment-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Add a comment…"
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-5 py-3 text-cream placeholder:text-cream/40 focus:outline-none focus:border-papaya transition-colors"
            />
            <button
              type="submit"
              data-testid="comment-submit"
              disabled={!text.trim() || sending}
              className="w-12 h-12 rounded-full bg-papaya hover:bg-papaya-hover text-ink flex items-center justify-center disabled:opacity-40 transition-colors"
            >
              <PaperPlaneRight size={20} weight="fill" />
            </button>
          </form>
        )}
      </div>
      <VoiceRecorderSheet open={pickVoice} onClose={() => setPickVoice(false)} onPick={sendVoice} title="Voice comment" />
    </div>
  );
}
